import { apiService } from "api/apiService";
import { toAxiosUploadProgress } from "shared/utils/axiosProgress";
import useHelpers from "composables/useHelpers.js";
import { EventEmitter } from "models/EventEmitter.js";
import { DEFAULT_SUCCESS_CRITERIA } from "models/learning-objectives/learningObjectivesConstants";

export class SuccessCriteria {
  constructor(data = {}) {
    const clonedDefaultSuccessCriteria = structuredClone(
      DEFAULT_SUCCESS_CRITERIA,
    );

    this.id = data.id ?? clonedDefaultSuccessCriteria.id;
    this.order = data.order ?? clonedDefaultSuccessCriteria.order;
    this.name = data.name ?? clonedDefaultSuccessCriteria.name;
    this.teacher_hints =
      data.teacher_hints ?? clonedDefaultSuccessCriteria.teacher_hints;
    this.attachments =
      data.attachments ?? clonedDefaultSuccessCriteria.attachments;
    this.links = data.links ?? clonedDefaultSuccessCriteria.links;
    this.videos = data.videos ?? clonedDefaultSuccessCriteria.videos;
    this.meta = data.meta ?? clonedDefaultSuccessCriteria.meta;
    this.is_temporary = !data.library_learning_objective_id;
    this.library_learning_objective_id =
      data.library_learning_objective_id ??
      clonedDefaultSuccessCriteria.library_learning_objective_id;
    this.events = new EventEmitter();
  }

  onDelete(callback) {
    this.events.on("deleted", callback);
  }

  async add({ onUploadProgress } = {}) {
    const { schoolId, rewriteModelToFormData } = useHelpers();
    const { library_learning_objective_id } = this;

    const formData = rewriteModelToFormData(this);

    const {
      data: { data: storedSuccessCriteria },
    } = await apiService.post(
      `api/school/${schoolId}/library-learning-objectives/${library_learning_objective_id}/library-success-criteria`,
      formData,
      { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
    );

    this.is_temporary = false;

    Object.assign(this, storedSuccessCriteria);

    return this;
  }

  async update(data, { onUploadProgress } = {}) {
    const { schoolId, rewriteModelToFormData } = useHelpers();

    const formData = rewriteModelToFormData(data);
    formData.set("_method", "PUT");

    const {
      data: { data: storedSuccessCriteria },
    } = await apiService.post(
      `/api/school/${schoolId}/library-learning-objectives/${this.library_learning_objective_id}/library-success-criteria/${this.id}`,
      formData,
      { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
    );

    Object.assign(this, storedSuccessCriteria);

    return storedSuccessCriteria;
  }

  async upsert(data, { onUploadProgress } = {}) {
    if (this.library_learning_objective_id === null && this.is_temporary) {
      // New success criteria without a parent yet — only stash the form
      // data locally; the parent learning-objective save will create it.
      Object.assign(this, data);
      return this;
    }

    if (this.is_temporary) {
      await this.add({ onUploadProgress });
      return this;
    }

    if (this.id) {
      await this.update(data, { onUploadProgress });
      return this;
    }
  }

  async remove() {
    try {
      if (!this.is_temporary) {
        const { schoolId } = useHelpers();
        const { id, library_learning_objective_id } = this;

        await apiService.delete(
          `api/school/${schoolId}/library-learning-objectives/${library_learning_objective_id}/library-success-criteria/${id}`,
        );
      }

      this.events.emit("deleted", this);
    } catch (error) {
      throw new Error(error);
    }
  }
}
